/* eslint-disable quotes */
import { Dispatch, ReactNode, SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';

// Types
import { WatchlistCategoryType } from 'services/watchlist/types';

export type DataItemType = {
  id: WatchlistCategoryType;
  icon: string;
  maxMonitoredItems: number;
  qaSelector: string;
};

type CategoryDataType = {
  title: string;
  data: DataItemType[];
  qaSelector: string;
};

export type WatchlistDataType = {
  personalInfromation: CategoryDataType;
  identification: CategoryDataType;
  financial: CategoryDataType;
  userName: CategoryDataType;
};

type DarkWebItemsMapType = {
  // eslint-disable-next-line no-unused-vars
  [K in WatchlistCategoryType]: {
    title: string;
    itemType: string;
    addItemTitle: string;
    whyMonitor: ReactNode;
    addItemSuccessMsg: string;
    addItemFailureMsg: string;
    removeItemTitle: string;
    removeItemSubTitle: string;
    removeItemSuccessMsg: string;
    removeItemFailureMsg: string;
  };
};

type EmailVerificationModalProps = {
  length: number;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
};

/* Component to render the FAQ route link */
function FAQLink() {
  return (
    <Link to='/faqs/darkwebFaqs' className='linkText'>
      FAQ
    </Link>
  );
}

export function ReviewLink({
  length,
  setIsModalOpen,
}: EmailVerificationModalProps) {
  const handleReviewBtn = () => {
    setIsModalOpen(true);
  };

  if (!length) return null;
  return (
    <div>
      Verification required for {length}
      {length === 1 ? ' email address. ' : ' email addresses. '}
      <Button type='link' className='alertLinkText' onClick={handleReviewBtn}>
        Review
      </Button>
    </div>
  );
}

const DARK_WEB_WATCH_LIST_DATA: WatchlistDataType = {
  personalInfromation: {
    title: 'Personal Information',
    qaSelector: 'component-dwm_personal-info',
    data: [
      {
        id: 'name',
        icon: 'user',
        maxMonitoredItems: 1,
        qaSelector: 'component-dwm_add-name',
      },
      {
        id: 'birthDate',
        icon: 'birthday',
        maxMonitoredItems: 1,
        qaSelector: 'component-dwm_add-dob',
      },
      {
        id: 'ssn',
        icon: 'ssn',
        maxMonitoredItems: 1,
        qaSelector: 'component-dwm_add-ssn',
      },
      {
        id: 'motherName',
        icon: 'adult',
        maxMonitoredItems: 1,
        qaSelector: 'component-dwm_add-mothers-maiden-name',
      },
      {
        id: 'email',
        icon: 'mail',
        maxMonitoredItems: 10,
        qaSelector: 'component-dwm_add-email',
      },
      {
        id: 'phone',
        icon: 'call',
        maxMonitoredItems: 10,
        qaSelector: 'component-dwm_add-phone',
      },
      {
        id: 'address',
        icon: 'home',
        maxMonitoredItems: 1,
        qaSelector: 'component-dwm_add-address',
      },
      {
        id: 'networkIP',
        icon: 'global-search',
        maxMonitoredItems: 10,
        qaSelector: 'component-dwm_add-ip-address',
      },
    ],
  },
  identification: {
    title: 'Identification Information',
    qaSelector: 'component-dwm_identification-info',
    data: [
      {
        id: 'license',
        icon: 'id',
        maxMonitoredItems: 10,
        qaSelector: 'component-dwm_add-drivers-license',
      },
      {
        id: 'insurance',
        icon: 'insurance',
        maxMonitoredItems: 10,
        qaSelector: 'component-dwm_add-health-insurance',
      },
      {
        id: 'passport',
        icon: 'passport',
        maxMonitoredItems: 10,
        qaSelector: 'component-dwm_add-passport',
      },
      {
        id: 'visa',
        icon: 'passport',
        maxMonitoredItems: 10,
        qaSelector: 'component-dwm_add-greencard',
      },
      {
        id: 'taxPayer',
        icon: 'document-user',
        maxMonitoredItems: 1,
        qaSelector: 'component-dwm_add-taxpayer-id',
      },
      {
        id: 'npi',
        icon: 'npi',
        maxMonitoredItems: 1,
        qaSelector: 'component-dwm_add-npi',
      },
    ],
  },
  financial: {
    title: 'Financial Information',
    qaSelector: 'component-dwm_financial-info',
    data: [
      {
        id: 'creditCard',
        icon: 'credit-card',
        maxMonitoredItems: 10,
        qaSelector: 'component-dwm_add-credit-card',
      },
      {
        id: 'accountNumber',
        icon: 'bank',
        maxMonitoredItems: 10,
        qaSelector: 'component-dwm_add-financial-account',
      },
      {
        id: 'loyalty',
        icon: 'loyalty-card',
        maxMonitoredItems: 10,
        qaSelector: 'component-dwm_add-loyalty-card',
      },
    ],
  },
  userName: {
    title: 'Username',
    qaSelector: 'component-dwm_username',
    data: [
      {
        id: 'gamertag',
        icon: 'controller',
        maxMonitoredItems: 2,
        qaSelector: 'component-dwm_add-gamertag',
      },
      {
        id: 'facebook',
        icon: 'facebook',
        maxMonitoredItems: 2,
        qaSelector: 'component-dwm_add-facebook',
      },
      {
        id: 'instagram',
        icon: 'instagram',
        maxMonitoredItems: 2,
        qaSelector: 'component-dwm_add-instagram',
      },
      {
        id: 'paypal',
        icon: 'paypal',
        maxMonitoredItems: 2,
        qaSelector: 'component-dwm_add-paypal',
      },
      {
        id: 'telegram',
        icon: 'telegram',
        maxMonitoredItems: 2,
        qaSelector: 'component-dwm_add-telegram',
      },
      {
        id: 'twitter',
        icon: 'x',
        maxMonitoredItems: 2,
        qaSelector: 'component-dwm_add-twitter',
      },
      {
        id: 'venmo',
        icon: 'venmo',
        maxMonitoredItems: 2,
        qaSelector: 'component-dwm_add-venmo',
      },
      {
        id: 'youtube',
        icon: 'youtube',
        maxMonitoredItems: 2,
        qaSelector: 'component-dwm_add-youtube',
      },
      {
        id: 'otherUsernames',
        icon: 'user-search',
        maxMonitoredItems: 10,
        qaSelector: 'component-dwm_add-others',
      },
    ],
  },
};

export const MEDICAL_WATCH_LIST_DATA: DataItemType[] = [
  {
    id: 'insurance',
    icon: 'insurance',
    maxMonitoredItems: 10,
    qaSelector: 'component-dwm_add-insurance',
  },
  {
    id: 'medicalRecordNumber',
    icon: 'medical-mrn',
    maxMonitoredItems: 10,
    qaSelector: 'component-dwm_add-mrn',
  },
  {
    id: 'medicalBeneficiaryIdentifier',
    icon: 'medical-mbi',
    maxMonitoredItems: 1,
    qaSelector: 'component-dwm_add-mbi',
  },
  {
    id: 'npi',
    icon: 'npi',
    maxMonitoredItems: 1,
    qaSelector: 'component-dwm_add-npi',
  },
];

export const DARK_WEB_ITEMS_MAP: DarkWebItemsMapType = {
  name: {
    title: 'Name',
    itemType: 'name',
    addItemTitle: 'Add Name',
    addItemSuccessMsg: 'Name added to Watchlist.',
    addItemFailureMsg:
      'Your name could not be added. Please wait and try again later.',
    whyMonitor:
      'Providing additional identifying information like name, address, and date of birth allows us to improve our matching, ensuring we alert you only about events that impact you.',
    removeItemTitle: 'Remove Name from your Watchlist?',
    removeItemSubTitle:
      'If you remove your name from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Name removed from Watchlist.',
    removeItemFailureMsg:
      'Name could not be removed. Please wait and try again later.',
  },
  birthDate: {
    title: 'Date of Birth',
    itemType: 'birth date',
    addItemTitle: 'Add Date of Birth',
    addItemSuccessMsg: 'Date of birth added to Watchlist',
    addItemFailureMsg:
      'Date of birth could not be added. Please wait and try again later.',
    whyMonitor:
      'Your date of birth is a critical piece of personally identifiable information. Fraudsters can use your date of birth and other personal information to access your accounts or open new accounts in your name.',
    removeItemTitle: 'Remove Date of birth from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Date of birth from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Date of birth removed from Watchlist.',
    removeItemFailureMsg:
      'Date of birth could not be removed. Please wait and try again later.',
  },
  ssn: {
    title: 'Social Security Number',
    itemType: 'ssn',
    addItemTitle: 'Add Social Security Number',
    addItemSuccessMsg: 'Social Security Number added to Watchlist.',
    addItemFailureMsg:
      'Social Security Number could not be added. Please wait and try again later.',
    whyMonitor:
      'Your Social Security Number (SSN) is the most crucial piece of government-issued identification, making it very valuable to identity thieves. A fraudster can use your SSN and other personal information to open credit card and bank accounts, take out loans, apply for government benefits, file a tax return, receive medical care, and obtain a job in your name.',
    removeItemTitle: 'Remove Social Security Number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Social Security Number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Social Security Number removed from Watchlist.',
    removeItemFailureMsg:
      'Social Security Number could not be removed. Please wait and try again later.',
  },
  motherName: {
    title: "Mother's Maiden Name",
    itemType: 'mothers maiden name',
    addItemTitle: "Add Mother's Maiden Name",
    addItemSuccessMsg: "Mother's maiden name added to Watchlist.",
    addItemFailureMsg:
      "Mother's maiden name could not be added. Please wait and try again later.",
    whyMonitor:
      "Mother's maiden name is often used as a security question or verification method by financial institutions, credit card companies, and other organizations. If a thief can obtain this information, they may use it to reset passwords, open new accounts, or make unauthorized purchases.",
    removeItemTitle: "Remove Mother's Maiden Name from your Watchlist?",
    removeItemSubTitle:
      "If you remove your mother's maiden name from your Watchlist, it will no longer be monitored.",
    removeItemSuccessMsg: "Mother's Maiden Name removed from Watchlist.",
    removeItemFailureMsg:
      "Mother's Maiden Name could not be removed. Please wait and try again later.",
  },
  email: {
    title: 'Email Address',
    itemType: 'email',
    addItemTitle: 'Add Email Address',
    addItemSuccessMsg: 'Email address added to Watchlist.',
    addItemFailureMsg:
      'Email address could not be added. Please wait and try again later.',
    whyMonitor:
      'Besides your name, your email address is the most public piece of your digital identity. Your login credentials may be compromised if your email address is found together with a password on the dark web. A hacker can use this information to access any account associated with these credentials.',
    removeItemTitle: 'Remove Email address from your Watchlist?',
    removeItemSubTitle:
      'If you remove this email address from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Email address removed from Watchlist.',
    removeItemFailureMsg:
      'Email address could not be removed. Please wait and try again later.',
  },
  phone: {
    title: 'Phone Number',
    itemType: 'phone',
    addItemTitle: 'Add Phone Number',
    addItemSuccessMsg: 'Phone number added to Watchlist.',
    addItemFailureMsg:
      'Phone number could not be added. Please wait and try again later.',
    whyMonitor:
      'A criminal can use your phone number and other personal information for impersonation or fraud. For example, a hacker opens an account with a different carrier, then transfers your number to a new phone. From there, they could attempt to take over your email, financial, or social media accounts to access sensitive information.',
    removeItemTitle: 'Remove Phone number from your Watchlist?',
    removeItemSubTitle:
      'If you remove phone number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Phone number removed from Watchlist.',
    removeItemFailureMsg:
      'Phone number could not be removed. Please wait and try again later.',
  },
  address: {
    title: 'Home Address',
    itemType: 'address',
    addItemTitle: 'Add Home Address',
    addItemSuccessMsg: 'Home address added to Watchlist.',
    addItemFailureMsg:
      'Home address could not be added. Please wait and try again later.',
    whyMonitor:
      'A criminal can use your home address and other personal information for impersonation or fraud. Searching for just your name and home address in public records, a thief can find the additional personal information necessary to steal your identity.',
    removeItemTitle: 'Remove Home address from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Home address from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Home address removed from Watchlist.',
    removeItemFailureMsg:
      'Home address could not be removed. Please wait and try again later.',
  },
  networkIP: {
    title: 'IP Address',
    itemType: 'network ip',
    addItemTitle: 'Add IP Address',
    addItemSuccessMsg: 'IP address added to Watchlist.',
    addItemFailureMsg:
      'IP address could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        When a fraudster obtains your IP address, they may use it to try and
        gain access to your accounts through a process known as &quot;IP address
        spoofing.&quot; The attacker makes it appear as though their own IP
        address is the same as yours, which may allow them to bypass some of the
        security measures in place to protect accounts.
        <br />
        <br />
        If you need help finding your IP address, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove IP Address from your Watchlist?',
    removeItemSubTitle:
      'If you remove this IP address from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'IP Address removed from Watchlist.',
    removeItemFailureMsg:
      'IP Address could not be removed. Please wait and try again later.',
  },
  license: {
    title: "Driver's License Number",
    itemType: 'driver license',
    addItemTitle: "Add Driver's License Number",
    addItemSuccessMsg: "Driver's license number added to Watchlist.",
    addItemFailureMsg:
      "Driver's license number could not be added. Please wait and try again later.",
    whyMonitor:
      "A criminal can use your driver's license number for impersonation or fraud. In many states, criminals can find your full name, address, date of birth, and social security number just by searching your driver's license number. Thieves can use your driver's license number to create a fake ID or commit check fraud. During a traffic stop, an impostor can give your driver's license number to a police officer, which could negatively impact your driving record. Law enforcement agencies report that 6% of traffic violations involve some form of driver's license fraud.",
    removeItemTitle: "Remove Driver's license number from your Watchlist?",
    removeItemSubTitle:
      "If you remove your driver's license number from your Watchlist, it will no longer be monitored.",
    removeItemSuccessMsg: "Driver's license number removed from Watchlist.",
    removeItemFailureMsg:
      "Driver's license number could not be removed. Please wait and try again later.",
  },
  insurance: {
    title: 'Health Insurance ID Number',
    itemType: 'health info',
    addItemTitle: 'Add Health Insurance ID Number',
    addItemSuccessMsg: 'Health Insurance ID Number added to Watchlist.',
    addItemFailureMsg:
      'Health Insurance ID Number could not be added. Please wait and try again later.',
    whyMonitor:
      'A criminal can use your health insurance ID number and other personal information for impersonation or fraud. A fraudster can secure medical services, get prescription drugs, or file insurance claims using your unique health insurance ID number. If the thief’s medical information gets mixed in your electronic health records, you may receive improper care. If the impostor sees a doctor and gets treatment, their unpaid medical bills could go to collections, negatively impacting your credit history.',
    removeItemTitle: 'Remove Health insurance ID number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your health insurance ID from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Health insurance ID number removed from Watchlist.',
    removeItemFailureMsg:
      'Health insurance ID number could not be removed. Please wait and try again later.',
  },
  passport: {
    title: 'Passport Number',
    itemType: 'passport',
    addItemTitle: 'Add Passport Number',
    addItemSuccessMsg: 'Passport number added to Watchlist.',
    addItemFailureMsg:
      'Passport number could not be added. Please wait and try again later.',
    whyMonitor:
      'Having your passport exposed on the Dark Web may mean that your passport was stolen or copied. A stolen passport is especially dangerous because it can lead to identity theft on an international level. For example, a thief can alter your passport or forge a new one using your number and their photo to open financial accounts, transfer money, or travel internationally under your name.',
    removeItemTitle: 'Remove Passport number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Passport number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Passport number removed from Watchlist.',
    removeItemFailureMsg:
      'Passport number could not be removed. Please wait and try again later.',
  },
  visa: {
    title: 'Green Card/Visa Number',
    itemType: 'visa number',
    addItemTitle: 'Add Green Card or Visa Number',
    addItemSuccessMsg: 'Green Card/Visa Number added to Watchlist.',
    addItemFailureMsg:
      'Green Card/Visa Number could not be added. Please wait and try again later.',
    whyMonitor:
      'If your Green Card or Visa number is stolen, it could be used for identity theft, fraud or other illegal activities. Protecting your personal information and being cautious about sharing it online or with strangers is important.',
    removeItemTitle: 'Remove Green Card/Visa Number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Green Card/Visa Number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Green Card/Visa Number removed from Watchlist.',
    removeItemFailureMsg:
      'Green Card/Visa Number could not be removed. Please wait and try again later.',
  },
  taxPayer: {
    title: 'Taxpayer Identification Number',
    itemType: 'tax id',
    addItemTitle: 'Add Taxpayer Identification Number',
    addItemSuccessMsg: 'Taxpayer Identification Number added to Watchlist.',
    addItemFailureMsg:
      'Taxpayer Identification Number could not be added. Please wait and try again later.',
    whyMonitor:
      'An Individual Taxpayer Identification Number (ITIN or TIN) is a tax processing number only available for certain nonresident and resident aliens, their spouses, and dependents who cannot get a Social Security Number (SSN). If your TIN is stolen, it could be used by the thief to open bank accounts, apply for credit, or file fraudulent tax returns. This can result in financial losses for you and can also damage your credit score.',
    removeItemTitle:
      'Remove  Taxpayer Identification Number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Taxpayer Identification Number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg:
      'Taxpayer Identification Number removed from Watchlist.',
    removeItemFailureMsg:
      'Taxpayer Identification Number could not be removed. Please wait and try again later.',
  },
  npi: {
    title: 'National Provider Identifier',
    itemType: 'national provider identifier',
    addItemTitle: 'Add National Provider Identifier',
    addItemSuccessMsg: 'National Provider Identifier added to Watchlist.',
    addItemFailureMsg:
      'National Provider Identifier could not be added. Please wait and try again later.',
    whyMonitor:
      'According to Physicians Practice, "Thousands of National Provider Identifiers (NPIs) are stolen from healthcare professionals and used for further fraudulent schemes every year, particularly Medicaid and Medicare fraud." Recovering from misuse of your NPI, including resolving potential financial liabilities, can be a long and difficult process.',
    removeItemTitle: 'Remove National Provider Identifier from your Watchlist?',
    removeItemSubTitle:
      'If you remove your National Provider Identifier from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg:
      'National Provider Identifier removed from Watchlist.',
    removeItemFailureMsg:
      'National Provider Identifier could not be removed. Please wait and try again later.',
  },
  creditCard: {
    title: 'Credit Card Number',
    itemType: 'credit card',
    addItemTitle: 'Add Credit Card Number',
    addItemSuccessMsg: 'Credit card number added to Watchlist.',
    addItemFailureMsg:
      'Credit card number could not be added. Please wait and try again later.',
    whyMonitor:
      'Credit card fraud is a multi-billion dollar a year industry. Criminals may use your credit card number to make purchases online or in stores. Many of these fraudulent purchases go undetected.',
    removeItemTitle: 'Remove Credit card number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your credit card number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Credit card number removed from Watchlist.',
    removeItemFailureMsg:
      'Credit card number could not be removed. Please wait and try again later.',
  },
  accountNumber: {
    title: 'Financial Account Number',
    itemType: 'financial account',
    addItemTitle: 'Add Financial Account Number',
    addItemSuccessMsg: 'Financial account number added to Watchlist.',
    addItemFailureMsg:
      'Financial account number could not be added. Please wait and try again later.',
    whyMonitor:
      'A thief may use your financial account number for impersonation or fraud. With your financial account number and other personal information in hand, a fraudster can take over your account and drain your funds. Recovery from this type of financial fraud is often time-consuming and costly, especially if it is not detected quickly.',
    removeItemTitle: 'Remove Financial account number from your Watchlist?',
    removeItemSubTitle:
      'If you remove this financial account number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Financial account number removed from Watchlist.',
    removeItemFailureMsg:
      'Financial account number could not be removed. Please wait and try again later.',
  },
  loyalty: {
    title: 'Loyalty Card Number',
    itemType: 'loyalty card number',
    addItemTitle: 'Add Loyalty Card Number',
    addItemSuccessMsg: 'Loyalty card number added to Watchlist.',
    addItemFailureMsg:
      'Loyalty card number could not be added. Please wait and try again later.',
    whyMonitor:
      'Loyalty points are essentially the equivalent of cash. They can be exchanged for goods and services and in some cases exchanged for money. As a result, fraudsters are increasingly targeting loyalty accounts.',
    removeItemTitle: 'Remove Loyalty card number from your Watchlist?',
    removeItemSubTitle:
      'If you remove this loyalty card number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Loyalty card number removed from Watchlist.',
    removeItemFailureMsg:
      'Loyalty card number could not be removed. Please wait and try again later.',
  },
  gamertag: {
    title: 'Gamertag',
    itemType: 'gamer tag',
    addItemTitle: 'Add Gamertag',
    addItemSuccessMsg: 'Gamertag added to Watchlist.',
    addItemFailureMsg:
      'Gamertag could not be added. Please wait and try again later.',
    whyMonitor:
      "A typical gaming account can contain a player's name, birth year, mailing address, email, mobile number, payment information, and other personal information that, if obtained by an identity thief, can cause financial damage. Cybercriminals also target gamer tags for virtual items to sell on the Dark Web. Once a gaming account is compromised, the player's other accounts, including banking and social media, are at a higher risk for account takeovers and fraud.",
    removeItemTitle: 'Remove Gamertag from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Gamertag from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Gamertag removed from Watchlist.',
    removeItemFailureMsg:
      'Gamertag could not be removed. Please wait and try again later.',
  },
  facebook: {
    title: 'Facebook',
    itemType: 'online profile facebook',
    addItemTitle: 'Add Facebook Username',
    addItemSuccessMsg: 'Facebook username added to Watchlist.',
    addItemFailureMsg:
      'Facebook username could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        You can sign in to your Facebook account using the email, phone number,
        or username that you signed up with, along with your password.
        Monitoring your Facebook account details, including your username, may
        help detect exposure of your Facebook account credentials. When your
        credentials are exposed the risk of unauthorized or fraudulent access to
        your account increases.
        <br />
        <br />
        If you need help finding your Facebook username, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove Facebook Username from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Facebook username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Facebook Username removed from Watchlist.',
    removeItemFailureMsg:
      'Facebook Username could not be removed. Please wait and try again later.',
  },
  instagram: {
    title: 'Instagram',
    itemType: 'online profile instagram',
    addItemTitle: 'Add Instagram Username',
    addItemSuccessMsg: 'Instagram username added to Watchlist.',
    addItemFailureMsg:
      'Instagram username could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Monitoring your Instagram account details, including your username, may
        help detect exposure of your account credentials. When your credentials
        are exposed the risk of unauthorized or fraudulent access to your
        account increases. It also means you&apos;re more likely to be a target
        of spamming, social engineering, and phishing attacks.
        <br />
        <br />
        If you need help finding your Instagram username, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove Instagram Username from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Instagram username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Instagram Username removed from Watchlist.',
    removeItemFailureMsg:
      'Instagram Username could not be removed. Please wait and try again later.',
  },
  paypal: {
    title: 'PayPal',
    itemType: 'online profile paypal',
    addItemTitle: 'Add PayPal Username',
    addItemSuccessMsg: 'PayPal username added to Watchlist.',
    addItemFailureMsg:
      'PayPal username could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        PayPal allows users can link their bank account, credit card, or debit
        card to the platform and then use it to make payments online or through
        a mobile device
        <br />
        <br />
        Your PayPal username is a unique identifier that you choose when you
        create your PayPal account.
        <br />
        <br />
        Monitoring your PayPal account details, including your username, may
        help detect exposure of your account credentials. When your credentials
        are exposed the risk of unauthorized or fraudulent access to your
        account increases.
        <br />
        <br />
        If you need help finding your Paypal username, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove PayPal Username from your Watchlist?',
    removeItemSubTitle:
      'If you remove your PayPal username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'PayPal Username removed from Watchlist.',
    removeItemFailureMsg:
      'PayPal Username could not be removed. Please wait and try again later.',
  },
  telegram: {
    title: 'Telegram',
    itemType: 'online profile telegram',
    addItemTitle: 'Add Telegram Username',
    addItemSuccessMsg: 'Telegram username added to Watchlist.',
    addItemFailureMsg:
      'Telegram username could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Telegram is one of the most secure messaging apps you can use. Setting
        up a username on Telegram allows you to use the app without sharing your
        phone number with others.
        <br />
        <br />
        Monitoring your Telegram account details, including your username, may
        help detect exposure of your account credentials. When your credentials
        are exposed the risk of unauthorized or fraudulent access to your
        account increases.
        <br />
        <br />
        If you need help finding your Telegram username, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove Telegram Username from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Telegram username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Telegram Username removed from Watchlist.',
    removeItemFailureMsg:
      'Telegram Username could not be removed. Please wait and try again later.',
  },
  twitter: {
    title: 'Twitter',
    itemType: 'online profile twitter',
    addItemTitle: 'Add Twitter Handle',
    addItemSuccessMsg: 'Twitter Handle added to Watchlist.',
    addItemFailureMsg:
      'Twitter Handle could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Your Twitter username, also known as your handle, begins with the “@”
        symbol, is unique to your account, and appears in your Twitter profile
        URL. Your username is used to log in to your account, and is visible
        when sending and receiving replies and Direct Messages.
        <br />
        <br />
        Monitoring your Twitter account details, including your handle, may help
        detect exposure of your Twitter account credentials. When your
        credentials are exposed the risk of unauthorized or fraudulent access to
        your account increases.
        <br />
        <br />
        If you need help finding your Twitter handle, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove Twitter Handle from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Twitter Handle from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Twitter Handle removed from Watchlist.',
    removeItemFailureMsg:
      'Twitter Handle could not be removed. Please wait and try again later.',
  },
  venmo: {
    title: 'Venmo',
    itemType: 'online profile venmo',
    addItemTitle: 'Add Venmo Username',
    addItemSuccessMsg: 'Venmo username added to Watchlist.',
    addItemFailureMsg:
      'Venmo username could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        You can sign in to your Venmo account using the email, phone number, or
        username that you signed up with, along with your password. Monitoring
        your Venmo account, including your username, may help detect exposure of
        your Venmo account credentials, putting you at risk of unauthorized or
        fraudulent access to your account.
        <br />
        <br />
        If you need help finding your Venmo username, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove Venmo Username from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Venmo username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Venmo Username removed from Watchlist.',
    removeItemFailureMsg:
      'Venmo Username could not be removed. Please wait and try again later.',
  },
  youtube: {
    title: 'YouTube',
    itemType: 'online profile youtube',
    addItemTitle: 'Add YouTube Handle',
    addItemSuccessMsg: 'YouTube Handle added to Watchlist.',
    addItemFailureMsg:
      'YouTube Handle could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Monitoring your YouTube account details, including your handle, may help
        detect exposure of your account credentials. When your credentials are
        exposed the risk of unauthorized or fraudulent access to your account
        increases. It also means you&apos;re more likely to be a target of
        spamming, social engineering, and phishing attacks.
        <br />
        <br />
        If you need help finding your YouTube handle, see our <FAQLink />.
      </>
    ),
    removeItemTitle: 'Remove YouTube Handle from your Watchlist?',
    removeItemSubTitle:
      'If you remove your YouTube handle from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'YouTube Handle removed from Watchlist.',
    removeItemFailureMsg:
      'YouTube Handle could not be removed. Please wait and try again later.',
  },
  otherUsernames: {
    title: 'Other Usernames',
    itemType: 'username',
    addItemTitle: 'Add Username',
    addItemSuccessMsg: 'Username added to Watchlist.',
    addItemFailureMsg:
      'Username could not be added. Please wait and try again later.',
    whyMonitor:
      'While many sites use your email address as a login credential, others require you to create a username. For example, many banks and other financial institutions use a username instead of an email address to access online account information and make transactions. By registering your username, we can notify you if we find it exposed on the Dark Web.',
    removeItemTitle: 'Remove Username from your Watchlist?',
    removeItemSubTitle:
      'If you remove this username from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Username removed from Watchlist.',
    removeItemFailureMsg:
      'Username could not be removed. Please wait and try again later.',
  },
  medicalRecordNumber: {
    title: 'Medical Record Number',
    itemType: 'medical record number',
    addItemTitle: 'Add Medical Record Number',
    addItemSuccessMsg: 'Medical Record Number added to Watchlist.',
    addItemFailureMsg:
      'Medical Record Number could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Medical record numbers (MRNs) are unique identifiers assigned to
        patients by healthcare organizations to keep track of their medical
        records. These numbers can be used to link all the different parts of a
        patient&apos;s medical history, including their test results,
        medications, treatments, and other personal information.
        <br />
        <br />
        We recommend monitoring your medical record number because the records
        often contain sensitive information such as Social Security numbers,
        insurance policy numbers, and credit card information that can be used
        for identity theft. Additionally, your medical history is extremely
        personal and you should be aware if it has been exposed.
        <br />
        <br />
        It&apos;s important to note that MRNs are not unique across all
        healthcare organizations, and they may not be shared between
        organizations. If you receive treatment from multiple healthcare
        providers, you may have multiple MRNs, one for each provider you see.
      </>
    ),
    removeItemTitle: 'Remove Medical Record Number from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Medical Record Number from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg: 'Medical Record Number removed from Watchlist.',
    removeItemFailureMsg:
      'Medical Record Number could not be removed. Please wait and try again later.',
  },
  medicalBeneficiaryIdentifier: {
    title: 'Medical Beneficiary Identifier',
    itemType: 'medical beneficiary identifier',
    addItemTitle: 'Add Medical Beneficiary Identifier',
    addItemSuccessMsg: 'Medical Beneficiary Identifier added to Watchlist.',
    addItemFailureMsg:
      'Medical Beneficiary Identifier could not be added. Please wait and try again later.',
    whyMonitor: (
      <>
        Medical Beneficiary Identifier (MBI) is the number assigned to Medicare
        beneficiaries by the Centers for Medicare and Medicaid Services (CMS) to
        identify the beneficiary. Monitoring the dark web for your MBI is
        important because it can be used to access sensitive personal
        information such as medical records, financial information, and personal
        identifying information. If your MBI is found on the dark web, it may
        indicate that your data has been compromised and is being sold or used
        for illegal activities. By monitoring the dark web for your MBI, you can
        identify if your personal information has been compromised and take
        appropriate action to protect yourself.
      </>
    ),
    removeItemTitle:
      'Remove Medical Beneficiary Identifier from your Watchlist?',
    removeItemSubTitle:
      'If you remove your Medical Beneficiary Identifier from your Watchlist, it will no longer be monitored.',
    removeItemSuccessMsg:
      'Medical Beneficiary Identifier removed from Watchlist.',
    removeItemFailureMsg:
      'Medical Beneficiary Identifier could not be removed. Please wait and try again later.',
  },
};

export const EXCLUDE_WATCH_LIST_ITEMS_FOR_PS_GLOBAL: WatchlistCategoryType[] = [
  'ssn',
  'address',
  'license',
  'visa',
  'taxPayer',
  'npi',
  'loyalty',
];

export const EXCLUDE_WATCH_LIST_ITEMS_FOR_HEALTH_CARE_FEATURE: WatchlistCategoryType[] =
  ['insurance', 'npi'];

export type WatchlistDataKeyType = keyof WatchlistDataType;
export default DARK_WEB_WATCH_LIST_DATA;
